import { useContext } from 'react';
import { useAppSelector } from '@/features';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Button } from '@/shared/components/ui/button';
import { Divider, Tooltip } from '@mui/material';
import CircularProgress from 'components/CircularProgress';
import { CHARGING_PERMISSION } from 'constants/ChargingPermission';
import { getChargePointByIdSelector } from 'features/ChargePoints';
import { getDesyncedChargingConnectorIdsSelector } from 'features/Connectors';
import { shallowEqual, useSelector } from 'react-redux';
import { type Connector, type Vehicle } from 'types/swagger';
import { getClassByStatus } from 'util/getComponentIcon';

import BatteryInfo from './BateryInfo';
import ConnectorTitle from './ConnectorTitle';
import FinalSoc from './FinalSoc';
import ManualFields from './ManualFields';
import NoTransactionWarning from './NoTransactionWarning';
import LongWarningContent from './NoTransactionWarning/LongWarningContent';
import { ConnectorDetailsContainer, ConnectorInfoContainer } from './styled';
import TransactionValues, { type TransactionValuesProps } from './TransactionValues';
import VehicleInfo from './VehicleInfo';

type ConnectorFinishingProps = {
  connector: Connector;
  isLoading: boolean;
  selectedVehicle: Vehicle | null;
  setSelectedVehicle: (vehicle: Vehicle) => void;
  odometerValue: string;
  setOdometerValue: (value: string) => void;
  socLimit: number;
  setSocLimit: (value: number) => void;
  prevSocLimit: number;
  handleUpdateCharge: () => void;
  handleToggleCharge: () => Promise<void>;
} & TransactionValuesProps;

const ConnectorFinishing = ({
  status,
  vehicle,
  connector,
  batterySoc,
  autonomy,
  donutData,
  absoluteSoc,
  capacity,
  power,
  chargedEnergy,
  startTime,
  endTime,
  transaction,
  estimatedEndTime,
  remainingTime,
  isLoading,
  selectedVehicle,
  setSelectedVehicle,
  odometerValue,
  setOdometerValue,
  socLimit,
  setSocLimit,
  prevSocLimit,
  handleUpdateCharge,
  handleToggleCharge,
}: ConnectorFinishingProps) => {
  const { user } = useContext(UserSessionContext);
  const chargePoint = useAppSelector(
    (state) => (connector ? getChargePointByIdSelector(state, connector.charge_point) : null),
    shallowEqual
  );

  const desyncedChargingConnectorIds = useSelector(getDesyncedChargingConnectorIdsSelector);
  const connectorHasDesync = connector ? desyncedChargingConnectorIds.includes(connector.id) : true;

  return (
    <ConnectorDetailsContainer className="flex-fill">
      <ConnectorTitle connectorName={connector?.verbose_name || '- - -'} />
      <ConnectorInfoContainer className={getClassByStatus(status as string)}>
        {vehicle ? (
          <VehicleInfo connector={connector} status={status} />
        ) : (
          <BatteryInfo connector={connector} status={status} />
        )}
      </ConnectorInfoContainer>

      {connectorHasDesync ? (
        <NoTransactionWarning
          title="Ocurrió un problema al obtener los datos del cargador."
          customContent={<LongWarningContent />}
        />
      ) : (
        <TransactionValues
          batterySoc={batterySoc}
          autonomy={autonomy}
          donutData={donutData}
          absoluteSoc={absoluteSoc}
          capacity={capacity}
          power={power}
          chargedEnergy={chargedEnergy}
          startTime={startTime}
          endTime={endTime}
          status={status}
          transaction={transaction}
          estimatedEndTime={estimatedEndTime}
          remainingTime={remainingTime}
        />
      )}

      <div className="d-flex flex-column flex-fill">
        {!isLoading ? (
          <div className="d-flex flex-column flex-fill justify-content-end">
            {chargePoint && !chargePoint.fill_manual_fields ? null : (
              <ManualFields
                selectedVehicle={selectedVehicle}
                setSelectedVehicle={setSelectedVehicle}
                odometerValue={odometerValue}
                setOdometerValue={setOdometerValue}
              />
            )}
            <Divider className="my-3" />
            <FinalSoc
              disabled={!user?.groups?.includes(CHARGING_PERMISSION)}
              socLimit={socLimit}
              setSocLimit={setSocLimit}
            />
            <Tooltip
              disableInteractive
              disableHoverListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
              disableTouchListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
              title="Usted no tiene permisos suficientes para gestionar cargas. Contacte a un administrador."
            >
              <div>
                <Tooltip
                  disableInteractive
                  disableHoverListener={
                    !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                  }
                  disableTouchListener={
                    !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                  }
                  title="Cambie el valor de SoC máximo para volver a actualizarlo."
                  placement="top"
                >
                  <div className="mb-3">
                    <Button
                      variant="secondary"
                      className="$tw-w-full"
                      disabled={
                        !user?.groups?.includes(CHARGING_PERMISSION) || socLimit === prevSocLimit
                      }
                      onClick={handleUpdateCharge}
                    >
                      Actualizar SoC Máximo
                    </Button>
                  </div>
                </Tooltip>
                <Tooltip
                  disableInteractive
                  disableHoverListener={
                    !user?.groups?.includes(CHARGING_PERMISSION) ||
                    !(chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                  }
                  disableTouchListener={
                    !user?.groups?.includes(CHARGING_PERMISSION) ||
                    !(chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                  }
                  title="Debe llenar la identificación del vehículo antes de poder iniciar una carga"
                >
                  <div>
                    <Button
                      className="$tw-w-full"
                      disabled={
                        !user?.groups?.includes(CHARGING_PERMISSION) ||
                        (chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                      }
                      onClick={handleToggleCharge}
                    >
                      Iniciar carga
                    </Button>
                  </div>
                </Tooltip>
              </div>
            </Tooltip>
          </div>
        ) : (
          <div className="d-flex flex-column flex-fill justify-content-end">
            <CircularProgress />
          </div>
        )}
      </div>
    </ConnectorDetailsContainer>
  );
};

export default ConnectorFinishing;
